import MarkdownIt from 'markdown-it'
import mdKatex from '@traptitech/markdown-it-katex'
import mk from '@iktakahiro/markdown-it-katex'
import mila from 'markdown-it-link-attributes'
import hljs from 'highlight.js'
import { t } from '@/locales'

function highlightBlock(str: string, lang?: string) {
  return `<pre class="code-block-wrapper"><div class="code-block-header"><span class="code-block-header__lang">${lang}</span><span class="code-block-header__copy">${t('chat.copyCode')}</span></div><code class="hljs code-block-body ${lang}">${str}</code></pre>`
}

const mdi = new MarkdownIt({
  html: true,
  linkify: true,
  highlight(code, language) {
    const validLang = !!(language && hljs.getLanguage(language))
    if (validLang) {
      const lang = language ?? ''
      return highlightBlock(hljs.highlight(code, { language: lang }).value, lang)
    }
    return highlightBlock(hljs.highlightAuto(code).value, '')
  },
})

mdi.use(mila, { attrs: { target: '_blank', rel: 'noopener' } })
mdi.use(mdKatex, { blockClass: 'katexmath-block rounded-md p-[10px]', errorColor: ' #cc0000' })
mdi.use(mk, {
  throwOnError: false,
  strict: 'ignore',
  macros: {
    '\\f': '#1f(#2)',
  },
  displayMode: false,
  trust: true,
  fleqn: true,
})

function hasLatexFormula(text: string): boolean {
  if (!text) {
    return false
  }

  // 编译正则表达式一次并缓存,提高性能
  const patterns = {
    // 基础运算符和函数
    basicOperators: /\\(?:sum|prod|int|frac|sqrt|lim|inf)[a-zA-Z]*/,
    // 上下标
    scripts: /[_^](?:\{[^{}]*\}|[^\s{}])/,
    // 分数和根式
    fractions: /\\(?:frac|sqrt)\{[^{}]*\}\{[^{}]*\}/,
    // 括号和界定符
    delimiters: /\\(?:left|right)[\(\[\{\)\]\}]|\\[{}()\[\]]/,
    // 数学环境
    environments: /\\begin\{(?:matrix|pmatrix|bmatrix|cases|align|equation)\}[\s\S]*?\\end\{(?:matrix|pmatrix|bmatrix|cases|align|equation)\}/,
    // 希腊字母和特殊符号
    symbols: /\\(?:alpha|beta|gamma|delta|theta|pi|sigma|omega|infty|partial)[a-zA-Z]*/,
  }

  // 使用 for...of 循环提前返回,避免不必要的检查
  for (const pattern of Object.values(patterns)) {
    if (pattern.test(text)) {
      return true
    }
  }

  return false
}

function renderLatexFormula(text: string) {
  if (!hasLatexFormula(text)) {
    return text
  }

  // 使用 mdKatex 插件渲染公式
  try {
    const formulaRegex = /\[((?:[^[\]]|\[(?:[^[\]]|\[[^\[\]]*\])*\])*(?:\\[a-zA-Z]+|[_^]\{[^{}]*\}|[_^][^\s{}]|\\frac{.*?}{.*?}|\\sqrt{.*?}|\\left[\(\[\{].*?\\right[\)\]\}]|\\begin{.*?}.*?\\end{.*?}|\\[a-zA-Z]+{.*?})(?:[^[\]]|\[(?:[^[\]]|\[[^\[\]]*\])*\])*)\]|\(((?:[^()]|\((?:[^()]|\([^()]*\))*\))*(?:\\[a-zA-Z]+|[_^]\{[^{}]*\}|[_^][^\s{}]|\\frac{.*?}{.*?}|\\sqrt{.*?}|\\left[\(\[\{].*?\\right[\)\]\}]|\\begin{.*?}.*?\\end{.*?}|\\[a-zA-Z]+{.*?})(?:[^()]|\((?:[^()]|\([^()]*\))*\))*)\)/g
    const formulaParts = text.match(formulaRegex) || []

    // 如果没有找到公式,直接返回原文本
    if (formulaParts.length === 0) {
      return text
    }

    // 使用map来处理公式
    const renderedFormulas = new Map()
    formulaParts.forEach((formula) => {
      const cleanFormula = formula.slice(1, -1)
      // 检查是否已经渲染过相同的公式
      if (!renderedFormulas.has(cleanFormula)) {
        const rendered = mdi.render(`$$${cleanFormula.replace(/(?<!\\)%/g, '\\%')}$$`)
        renderedFormulas.set(cleanFormula, rendered)
      }
    })

    // 一次性替换所有公式
    return formulaParts.reduce((renderedText, formula) => {
      const cleanFormula = formula.slice(1, -1)
      return renderedText.replace(formula, renderedFormulas.get(cleanFormula))
    }, text)
  } catch (error) {
    console.error('LaTeX formula rendering error:', error)
    return text
  }
}

function removeBackticks(text) {
  return text.replace(/`([^`]*)`/g, '$1')
}

function wrapTablesInDiv(htmlString) {
  // 正则表达式匹配<table>到</table>，包括可能的嵌套
  const regex = /<table\b[^>]*>([\s\S]*?)(<\/table>)/g

  // 使用replace方法包裹每个匹配的<table>标签
  return htmlString.replace(regex, (match, content, closingTag) => {
    return `<div class="ai-table-wrapper">${match}</div>`
  })
}

const renderText = (text) => {
  const removedSource = text.replace(/\[信息来源页码?\[?\d+(?:,\d+)*\]?\]/g, '')
  // console.log('text', text)
  const latex = renderLatexFormula(removedSource)
  // console.log('latex', latex)
  const t = wrapTablesInDiv(latex)
  return t
}

export default () => {
  return (text: string) => {
    return renderText(mdi.render(text))
  }
}
